import { shrpaApi } from "./axios";
import DayjsExtended from "@logic/DayjsExtended";

// Types
import { PhotoGallerySource, PhotoGalleryCustomerData, PhotoGalleryTitleAndComment } from "@contracts/photosPageData";

export default {
  /**
   * The Uploading Date is required to determine whether to create a new group when the customer uploads new assets outside a group
   */
  async createMyUploadsGroup(
    customerId: string,
    title: string,
    comment = null as string | null,
    currentOperationSource: PhotoGallerySource,
    photoCredit = null as string | null,
    locationId = null as string | null
  ): Promise<PhotoGalleryCustomerData> {
    const uri = `/photo-gallery/v2/${customerId}/my-uploads/photo-groups`;

    const payload = JSON.stringify({
      title,
      comment,
      photoCredit,
      clientSideDateTime: DayjsExtended().format(),
      createdSource: currentOperationSource,
      locationId,
    });

    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.post<PhotoGalleryCustomerData>(uri, payload, config);

    return data;
  },

  /**
   * Returns the gallery a Location's uploads go to, creating it (named after the Location) if there isn't one yet.
   * Safe to call again after a failure: the server finds the existing gallery instead of creating a second one.
   */
  async ensureGroupForLocation(customerId: string, locationId: string, currentOperationSource?: PhotoGallerySource | null): Promise<PhotoGalleryCustomerData> {
    let uri = `/photo-gallery/v2/${customerId}/my-uploads/locations/${encodeURIComponent(locationId)}/photo-group`;
    if (currentOperationSource) {
      uri += `?currentOperationSource=${currentOperationSource}`;
    }

    const { data } = await shrpaApi.post<PhotoGalleryCustomerData>(uri);

    return data;
  },

  async updateMyUploadsGroupCommentAndTitle(
    customerId: string,
    photoGalleryId: string,
    title: string,
    comment: string | null,
    currentOperationSource: PhotoGallerySource | null,
    photoCredit = null as string | null,
    locationId = null as string | null
  ): Promise<PhotoGalleryCustomerData> {
    let uri = `/photo-gallery/v2/${customerId}/my-uploads/photo-groups/${photoGalleryId}/comment-and-title`;
    if (currentOperationSource) {
      uri += `?currentOperationSource=${currentOperationSource}`;
    }

    const payload = JSON.stringify({
      title,
      comment,
      photoCredit,
      locationId,
    } as PhotoGalleryTitleAndComment);

    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<PhotoGalleryCustomerData>(uri, payload, config);

    return data;
  },

  async addPhotosToGroup(customerId: string, photoGalleryId: string, photoIds: Array<string>, currentOperationSource?: PhotoGallerySource | null): Promise<void> {
    let uri = `/photo-gallery/v2/${customerId}/my-uploads/photo-groups/${photoGalleryId}/photos`;
    if (currentOperationSource) {
      uri += `?currentOperationSource=${currentOperationSource}`;
    }
    const payload = JSON.stringify(photoIds);
    const config = { headers: { "Content-Type": "application/json" } };

    await shrpaApi.put(uri, payload, config);
  },

  async deleteMyUploadsGroup(customerId: string, groupId: string, currentOperationSource?: PhotoGallerySource | null): Promise<void> {
    let uri = `/photo-gallery/v2/${customerId}/my-uploads/photo-groups/${groupId}`;
    if (currentOperationSource) {
      uri += `?currentOperationSource=${currentOperationSource}`;
    }

    await shrpaApi.delete<any>(uri);
  },

  async deleteMyUploadsGroupPhotos(customerId: string, photoIds: Array<string>, currentOperationSource?: PhotoGallerySource | null): Promise<void> {
    let uri = `/photo-gallery/v2/${customerId}/my-uploads/photos`;
    if (currentOperationSource) {
      uri += `?currentOperationSource=${currentOperationSource}`;
    }
    const payload = JSON.stringify(photoIds);
    const config = { headers: { "Content-Type": "application/json" } };

    await shrpaApi.delete<any>(uri, { data: payload, ...config });
  },

  async movePhotosToAGallery(customerId: string, photoIds: Array<string>, galleryIdToMoveTo: string): Promise<void> {
    const uri = `/photo-gallery/v2/${customerId}/my-uploads/photos/move`;

    const payload = JSON.stringify({
      photoIds,
      galleryIdToMoveTo,
      clientSideDateTime: DayjsExtended().format(),
    });

    const config = { headers: { "Content-Type": "application/json" } };

    await shrpaApi.put<Array<string>>(uri, payload, config);
  },
};
